import { Injectable, inject } from '@angular/core';
import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import { ToastService } from '@app/core/services/toast/toast.service';
import {
  AccountingExportGQL,
  AccountingExportQuery,
  GenerateAccountingExportGQL,
  GenerateAccountingExportMutation,
} from '@app/generated/graphql';
import { Apollo } from 'apollo-angular';
import { ApolloQueryResult, FetchResult } from '@apollo/client/core';
import { Observable, of } from 'rxjs';
import { filter, switchMap, tap, catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/browser';

@Injectable({
  providedIn: 'root',
})
export class AccountingExportService {
  private eventBus = inject(EventBusService);
  private apollo = inject(Apollo);
  private generateAccountingExportGQL = inject(GenerateAccountingExportGQL);
  private accountingExportGQL = inject(AccountingExportGQL);
  private toastService = inject(ToastService);

  constructor() {}

  public accountingExport$(): Observable<
    ApolloQueryResult<AccountingExportQuery>
  > {
    return this.getAccountingExportIdStream().pipe(
      switchMap((exportId: string) => {
        return this.fetchAccountingExport(exportId);
      }),
    );
  }

  public generateAccountingExport$(
    invoiceIds: string[],
  ): Observable<FetchResult<GenerateAccountingExportMutation>> {
    return this.apollo
      .mutate<GenerateAccountingExportMutation>({
        mutation: this.generateAccountingExportGQL.document,
        variables: { invoiceIds },
      })
      .pipe(
        tap(() => {
          this.toastService.showToast('Generating accounting export...');
        }),
        catchError((error) => {
          this.toastService.showErrorToast(
            'Failed to generate accounting export',
          );
          Sentry.captureException(error, {
            extra: {
              invoiceIds: invoiceIds,
            },
          });
          return of(null);
        }),
      );
  }

  private getAccountingExportIdStream(): Observable<string> {
    return this.eventBus
      .getChannel('RefetchAccountingExport', false)
      .pipe(filter((id: string) => typeof id === 'string' && id.length > 0));
  }

  private fetchAccountingExport(
    exportId: string,
  ): Observable<ApolloQueryResult<AccountingExportQuery>> {
    return this.apollo.query<AccountingExportQuery>({
      query: this.accountingExportGQL.document,
      variables: { id: exportId },
    });
  }
}
